import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, KeyRound, Trash2, AlertTriangle } from 'lucide-react';
import useAuthStore from '@/store/authStore';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import ImageUpload from '@/components/ui/ImageUpload';
import Modal from '@/components/ui/Modal';
import { maskPhone, isValidPhone, onlyDigits } from '@/lib/masks';

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const changePassword = useAuthStore((s) => s.changePassword);
  const deleteAccount = useAuthStore((s) => s.deleteAccount);

  // Perfil
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(maskPhone(user?.phone || ''));
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  // Senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErr, setPwdErr] = useState('');

  // Exclusão
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const saveProfile = async () => {
    setProfileMsg('');
    setProfileErr('');
    if (phone && !isValidPhone(phone)) return setProfileErr(t('common.invalidPhone'));
    setSavingProfile(true);
    try {
      await updateProfile({ name, phone: onlyDigits(phone) || null, avatar: avatar || null });
      setProfileMsg(t('account.profileSaved'));
    } catch (e) {
      setProfileErr(e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const submitPassword = async () => {
    setPwdMsg('');
    setPwdErr('');
    if (newPassword.length < 6) {
      setPwdErr(t('account.passwordShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdErr(t('account.passwordMismatch'));
      return;
    }
    setSavingPwd(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPwdMsg(t('account.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      setPwdErr(e.message);
    } finally {
      setSavingPwd(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteErr('');
    try {
      await deleteAccount();
      navigate('/login', { replace: true });
    } catch (e) {
      setDeleteErr(e.message);
      setDeleting(false);
    }
  };

  return (
    <>
      <PageHeader kicker="Movez" title={t('account.title')} />

      {/* Editar perfil */}
      <Card padding="lg">
        <div className="kicker" style={{ marginBottom: 14 }}>
          {t('account.editProfile')}
        </div>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ImageUpload
            label={t('account.avatar')}
            folder="avatars"
            round
            value={avatar}
            onChange={setAvatar}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <Input
            label={t('account.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('account.name')}
          />
          <Input
            label={t('account.phone')}
            value={phone}
            onChange={(e) => setPhone(maskPhone(e.target.value))}
            inputMode="tel"
            placeholder="(11) 90000-0000"
          />
        </div>
        <div style={{ marginTop: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <Button icon={Save} loading={savingProfile} onClick={saveProfile}>
            {t('common.save')}
          </Button>
          {profileMsg && <span style={{ color: 'var(--volt)', fontSize: 13 }}>{profileMsg}</span>}
          {profileErr && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{profileErr}</span>}
        </div>
      </Card>

      {/* Alterar senha */}
      <Card padding="lg" style={{ marginTop: 'var(--space-4)' }}>
        <div className="kicker" style={{ marginBottom: 14 }}>
          {t('account.changePassword')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
          <Input
            label={t('account.currentPassword')}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          <Input
            label={t('account.newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <Input
            label={t('account.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div style={{ marginTop: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <Button
            variant="outline"
            icon={KeyRound}
            loading={savingPwd}
            onClick={submitPassword}
            disabled={!currentPassword || !newPassword}
          >
            {t('account.changePassword')}
          </Button>
          {pwdMsg && <span style={{ color: 'var(--volt)', fontSize: 13 }}>{pwdMsg}</span>}
          {pwdErr && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{pwdErr}</span>}
        </div>
      </Card>

      {/* Excluir conta */}
      <Card
        padding="lg"
        style={{
          marginTop: 'var(--space-4)',
          borderColor: 'color-mix(in srgb, var(--danger) 35%, transparent)',
        }}
      >
        <div className="kicker" style={{ marginBottom: 8, color: 'var(--danger)' }}>
          {t('account.dangerZone')}
        </div>
        <p style={{ color: 'var(--mid)', fontSize: 13, marginBottom: 14, maxWidth: 560 }}>
          {t('account.deleteDescription')}
        </p>
        <Button variant="danger" icon={Trash2} onClick={() => setConfirmOpen(true)}>
          {t('account.deleteAccount')}
        </Button>
      </Card>

      <Modal
        open={confirmOpen}
        onClose={() => !deleting && setConfirmOpen(false)}
        kicker={t('account.dangerZone')}
        title={t('account.deleteAccount')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" icon={Trash2} loading={deleting} onClick={confirmDelete}>
              {t('account.deleteConfirm')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertTriangle size={22} strokeWidth={2} color="var(--danger)" style={{ flex: 'none' }} />
          <p style={{ color: 'var(--hi)', fontSize: 14, lineHeight: 1.5 }}>
            {t('account.deleteWarning')}
          </p>
        </div>
        {deleteErr && (
          <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 12 }}>{deleteErr}</div>
        )}
      </Modal>
    </>
  );
}
